import { createContext, useContext, type PropsWithChildren } from "react";

import type { ScoutInternalEventStatus } from "../hooks/scoutInternalEvents";
import type { ScoutDataChangeSummary } from "../hooks/types";
import type { ScoutConversationPrivateDocumentRow } from "../hooks/useScoutConversationPrivateDocuments";
import type { ScoutDataChangeUndoResult } from "./scoutDataChangeUndoFailure";

import {
  DATA_CHANGE_PROPOSAL_KIND,
  type ScoutDataChangeCardStatus,
  type ScoutDataChangeProposalOperation,
} from "./scoutDataChangeProposal";
import { LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND } from "./scoutLargeFileFullAnalysisProposal";
import { TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND } from "./scoutTenantDocumentProposal";

export type ScoutProposalResponseStatus = ScoutInternalEventStatus;

export type TenantDocumentIngestionProposalHandlers = {
  respondedProposals: Record<string, ScoutProposalResponseStatus>;
  isProcessing: boolean;
  onConfirm: (input: {
    proposalId: string;
    documentIds: string[];
    totalProposedCount: number;
  }) => Promise<void>;
  onDecline: (input: { proposalId: string; totalProposedCount: number }) => Promise<void>;
};

export type LargeFileFullAnalysisProposalHandlers = {
  respondedProposals: Record<string, ScoutProposalResponseStatus>;
  isProcessing: boolean;
  onConfirm: (input: {
    proposalId: string;
    privateDocumentIds: string[];
    documents: ScoutConversationPrivateDocumentRow[];
  }) => Promise<void>;
  onDecline: (input: { proposalId: string; privateDocumentIds: string[] }) => Promise<void>;
};

export type DataChangeProposalHandlers = {
  respondedProposals: Record<string, ScoutDataChangeCardStatus>;
  isProcessing: boolean;
  /**
   * Resolve one or many staged mutations in a single Scout turn. Used for
   * Accept all, Reject all, and flushing a multi-card stack after the last
   * per-card decision (mixed confirm/decline allowed).
   */
  onResolve: (input: {
    proposals: Array<{
      proposalId: string;
      title: string;
      status: ScoutProposalResponseStatus;
      operation?: ScoutDataChangeProposalOperation;
    }>;
  }) => Promise<void>;
  /**
   * Reverse already-applied changes from a history card (or the counted batch
   * undo on that turn's stack). Omitted on read-only surfaces. Offered without
   * Scout Pro so a change applied on a preview's last credit can be undone.
   */
  onUndo?: (changeIds: string[]) => Promise<ScoutDataChangeUndoResult[] | void>;
  /**
   * After a stale undo failure, send a visible user request plus hidden
   * change-id events so Scout can propose compensating data changes.
   * Omitted on read-only surfaces and for non-Pro callers.
   */
  onRetryStaleUndo?: (changeIds: string[]) => Promise<void>;
  /** Per-row undo fields from conversation history / stream patches. */
  dataChanges?: ScoutDataChangeSummary[];
};

/**
 * Runtime resolve handlers keyed by proposal kind. Cards / surfaces look up
 * their kind here for confirm/decline + responded-state. Add a key when
 * registering a new kind in `scoutProposalRegistry`.
 */
export type ScoutProposalHandlersByKind = {
  [TENANT_DOCUMENT_INGESTION_PROPOSAL_KIND]: TenantDocumentIngestionProposalHandlers;
  [LARGE_FILE_FULL_ANALYSIS_PROPOSAL_KIND]: LargeFileFullAnalysisProposalHandlers;
  [DATA_CHANGE_PROPOSAL_KIND]: DataChangeProposalHandlers;
};

export type ScoutProposalContextValue = {
  handlers: ScoutProposalHandlersByKind;
  /**
   * Staff Observe and other replay surfaces: hide Accept/Reject while still
   * supplying responded-proposal state for Approved/Rejected summaries.
   */
  readOnly?: boolean;
  /**
   * When true, Accept/Reject (and stack Accept all / Reject all) stay disabled.
   * Used while the agent turn is still running so multi-chunk proposal stacks
   * cannot be resolved before every card has been staged.
   */
  actionsDisabled?: boolean;
};

const ScoutProposalContext = createContext<ScoutProposalContextValue | undefined>(undefined);

export function ScoutProposalProvider({
  value,
  children,
}: PropsWithChildren<{ value: ScoutProposalContextValue }>) {
  return <ScoutProposalContext.Provider value={value}>{children}</ScoutProposalContext.Provider>;
}

/**
 * Kind-scoped handlers for an approval surface. Returns undefined when
 * rendered outside a provider (buttons stay locked / no-op).
 */
export function useScoutProposalHandlers<K extends keyof ScoutProposalHandlersByKind>(
  kind: K
): ScoutProposalHandlersByKind[K] | undefined {
  return useContext(ScoutProposalContext)?.handlers[kind];
}

/** True on staff Observe / replay surfaces that must not resolve proposals. */
export function useScoutProposalSurfaceReadOnly(): boolean {
  return useContext(ScoutProposalContext)?.readOnly === true;
}

/** True when proposal interaction buttons should stay disabled (e.g. turn running). */
export function useScoutProposalActionsDisabled(): boolean {
  return useContext(ScoutProposalContext)?.actionsDisabled === true;
}
